<template>
  <div class="dn-new">
    <!-- SWITCH TO OLD VERSION -->
    <div
      class="dn-switch-banner"
      @click="goToOldVersion()">
      <v-icon
        small
        color="#c1167c"
        class="mr-2">
        mdi-rocket-launch-outline
      </v-icon>
      <span class="dn-switch-banner__text">กลับไปหน้า Delivery Note เวอร์ชันเดิม</span>
      <span class="dn-switch-banner__cta">
        Switch to old version
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <line
            x1="5"
            y1="12"
            x2="19"
            y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </span>
    </div>

    <!-- PAGE HEADER -->
    <div class="dn-page-header">
      <div>
        <h1 class="dn-title">
          Delivery Notes
        </h1>
        <p class="dn-subtitle">
          Manage inbound delivery notes across warehouses
        </p>
      </div>
      <div class="dn-header-actions">
        <button
          class="dn-btn dn-btn--outline"
          :disabled="isDisabled || isDisabledByRole"
          @click="togglePODialog()">
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
          Create by PO
        </button>
        <button
          class="dn-btn dn-btn--primary"
          :disabled="isDisabled || isDisabledByRole"
          @click="createNote()">
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round">
            <line
              x1="12"
              y1="5"
              x2="12"
              y2="19" />
            <line
              x1="5"
              y1="12"
              x2="19"
              y2="12" />
          </svg>
          Create Note
        </button>
      </div>
    </div>

    <!-- CARD -->
    <div class="dn-card">
      <!-- TOOLBAR -->
      <div class="dn-toolbar">
        <div class="dn-field">
          <label class="dn-field__label">Warehouse</label>
          <v-autocomplete
            :value="warehouseSelected"
            class="dn-autocomplete"
            :items="warehouses"
            item-text="name"
            item-value="id"
            :menu-props="{ offsetY: true }"
            placeholder="Select warehouse"
            hide-details
            dense
            solo
            flat
            append-icon="mdi-chevron-down"
            @change="setWarehouse($event)" />
        </div>

        <div class="dn-spacer"></div>

        <div class="dn-search">
          <svg
            class="dn-search__icon"
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9aa5b1"
            stroke-width="2">
            <circle
              cx="11"
              cy="11"
              r="7" />
            <line
              x1="21"
              y1="21"
              x2="16.65"
              y2="16.65" />
          </svg>
          <input
            v-model="searchInput"
            class="dn-search__input"
            placeholder="Search code or note…"
            @input="onSearchInput()"
            @keydown.enter="onSearch()" />
        </div>
      </div>

      <!-- TABS + EXPORT/PRINT -->
      <div class="dn-tabs-row">
        <div class="dn-tabs">
          <button
            v-for="(tab, index) in tabsTitle"
            :key="tab.key"
            class="dn-tab"
            :class="{ 'dn-tab--active': tabs === index }"
            @click="changeTab(index)">
            {{ tab.label }}
            <span class="dn-tab__badge">
              <v-progress-circular
                v-if="loadingCount"
                indeterminate
                size="14"
                width="2" />
              <template v-else>{{ formatNumber(tab.count) }}</template>
            </span>
          </button>
        </div>
        <div class="dn-tabs-actions">
          <button
            class="dn-action-btn"
            :disabled="!hasSelection || exporting"
            @click="exportItems()">
            <v-progress-circular
              v-if="exporting"
              indeterminate
              size="16"
              width="2" />
            <svg
              v-else
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line
                x1="12"
                y1="15"
                x2="12"
                y2="3" />
            </svg>
            Export Item
          </button>
          <button
            class="dn-action-btn dn-action-btn--primary"
            :disabled="!canPrint"
            @click="printSelected()">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect
                x="6"
                y="14"
                width="12"
                height="8" />
            </svg>
            Print ({{ selectedCount }})
          </button>
        </div>
      </div>

      <!-- SELECTION BANNER -->
      <div
        v-if="hasSelection"
        class="dn-sel-banner">
        <span class="dn-sel-banner__text">{{ selectedCount }} selected</span>
        <div class="dn-spacer"></div>
        <button
          class="dn-sel-btn"
          @click="clearSelection()">
          Clear
        </button>
        <button
          v-if="canCancel"
          class="dn-sel-btn dn-sel-btn--danger"
          @click="cancelSelected()">
          Cancel selected
        </button>
        <button
          class="dn-sel-btn dn-sel-btn--solid"
          @click="printSelected()">
          Print selected
        </button>
      </div>

      <!-- TOP PAGINATION -->
      <div class="dn-pagination dn-pagination--top">
        <div class="dn-rows-per-page">
          <span class="dn-rows-per-page__label">Rows per page:</span>
          <v-menu
            offset-y
            :close-on-content-click="true">
            <template #activator="{ on, attrs }">
              <button
                class="dn-rows-per-page__btn"
                v-bind="attrs"
                v-on="on">
                {{ perPage }}
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#7b8794"
                  stroke-width="2.2"
                  stroke-linecap="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </template>
            <v-list dense>
              <v-list-item
                v-for="size in perPageOptions"
                :key="size"
                @click="setPerPage(size)">
                <v-list-item-title>{{ size }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <span class="dn-range">{{ rangeStart }}–{{ rangeEnd }} of {{ formatNumber(activeCount) }}</span>
        <div class="dn-pager">
          <button
            title="Previous"
            class="dn-pager__btn"
            :disabled="page <= 1 || getting"
            @click="prevPage()">
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            title="Next"
            class="dn-pager__btn"
            :disabled="page >= totalPages || getting"
            @click="nextPage()">
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>

      <!-- TABLE -->
      <div class="dn-table-scroll">
        <div class="dn-table">
          <!-- HEADER ROW -->
          <div class="dn-row dn-row--head">
            <div class="dn-cell-check">
              <div
                v-if="canSelect"
                class="dn-check"
                :class="{ 'dn-check--on': allSelected }"
                @click="toggleAll()">
                <svg
                  v-if="allSelected"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  stroke-width="3.2"
                  stroke-linecap="round"
                  stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
            <div class="dn-th">
              Document No.
            </div>
            <div class="dn-th dn-th--center">
              Create Date
            </div>
            <div class="dn-th dn-th--center">
              Due Date
            </div>
            <div class="dn-th">
              Note
            </div>
            <div class="dn-th dn-th--right">
              Actions
            </div>
          </div>

          <!-- DATA ROWS -->
          <div
            v-for="item in rows"
            :key="item.id"
            class="dn-row"
            :class="{ 'dn-row--selected': isSelected(item) }">
            <div class="dn-cell-check">
              <div
                v-if="canSelect"
                class="dn-check"
                :class="{ 'dn-check--on': isSelected(item) }"
                @click="toggleSelect(item)">
                <svg
                  v-if="isSelected(item)"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  stroke-width="3.2"
                  stroke-linecap="round"
                  stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>

            <div>
              <div class="dn-doc">
                <span
                  class="dn-doc__type"
                  :style="typeStyle(item)">{{ docType(item) }}</span>
                <a
                  class="dn-doc__code"
                  href="#"
                  @click.prevent="openDetail(item)">{{ docNumber(item) }}</a>
                <button
                  class="dn-doc__copy"
                  :title="copiedId === item.id ? 'Copied!' : 'Copy document no.'"
                  @click="copyCode(item)">
                  <svg
                    v-if="copiedId === item.id"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1f8a50"
                    stroke-width="2.4"
                    stroke-linecap="round"
                    stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <svg
                    v-else
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round">
                    <rect
                      x="9"
                      y="9"
                      width="13"
                      height="13"
                      rx="2"
                      ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </div>
              <div class="dn-loc">
                <span
                  v-if="sourceChip(item)"
                  class="dn-loc__chip"
                  :style="chipStyle('origin')">
                  {{ sourceChip(item).name }}
                </span>
                <svg
                  v-if="sourceChip(item)"
                  class="dn-loc__arrow"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#b6c0c9"
                  stroke-width="2.4"
                  stroke-linecap="round"
                  stroke-linejoin="round">
                  <line
                    x1="5"
                    y1="12"
                    x2="19"
                    y2="12" />
                  <polyline points="13 6 19 12 13 18" />
                </svg>
                <span
                  class="dn-loc__chip"
                  :style="chipStyle(sourceChip(item) ? 'dest' : 'origin')">
                  {{ currentWarehouseName(item) }}
                </span>
              </div>
            </div>

            <div class="dn-cell--center">
              <div class="dn-date">
                {{ formatDate(item.createdAt) }}
              </div>
              <div class="dn-time">
                {{ formatTime(item.createdAt) }}
              </div>
            </div>

            <div class="dn-cell--center">
              <div
                v-if="noteStatus === 'pending' && item.dueDate"
                class="dn-date dn-due"
                :class="dueDateClass(item.dueDate)">
                {{ formatDate(item.dueDate) }}
              </div>
              <div
                v-else
                class="dn-date dn-date--empty">
                —
              </div>
            </div>

            <div
              class="dn-note"
              :title="noteCompute(item)">
              {{ noteCompute(item) }}
            </div>

            <div class="dn-actions">
              <button
                :title="item.autoDeliveryNoteId ? 'Allocated' : 'Allocation'"
                class="dn-icon-btn dn-icon-btn--accent"
                :class="{ 'dn-icon-btn--allocated': item.autoDeliveryNoteId && !disableAllowCate }"
                :disabled="disableAllowCate"
                @click="openAllocation(item)">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round">
                  <path d="M16 3h5v5" />
                  <path d="M8 3H3v5" />
                  <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" />
                  <path d="m21 3-7.828 7.828A4 4 0 0 0 12 13.7" />
                </svg>
              </button>
              <button
                title="Print"
                class="dn-icon-btn"
                :class="{ 'dn-icon-btn--printed': item.print }"
                @click="printOne(item)">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect
                    x="6"
                    y="14"
                    width="12"
                    height="8" />
                </svg>
              </button>
            </div>
          </div>

          <!-- LOADING -->
          <div
            v-if="getting"
            class="dn-empty">
            <v-progress-circular
              indeterminate
              color="#0f5d7c"
              size="28" />
          </div>

          <!-- EMPTY -->
          <div
            v-else-if="isEmpty"
            class="dn-empty">
            <div class="dn-empty__title">
              No delivery notes found
            </div>
            <div class="dn-empty__sub">
              Try a different search or tab.
            </div>
          </div>
        </div>
      </div>

      <!-- FOOTER PAGINATION -->
      <div class="dn-pagination dn-pagination--footer">
        <div class="dn-rows-per-page">
          <span class="dn-rows-per-page__label">Rows per page:</span>
          <v-menu
            offset-y
            top
            :close-on-content-click="true">
            <template #activator="{ on, attrs }">
              <button
                class="dn-rows-per-page__btn"
                v-bind="attrs"
                v-on="on">
                {{ perPage }}
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#7b8794"
                  stroke-width="2.2"
                  stroke-linecap="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </template>
            <v-list dense>
              <v-list-item
                v-for="size in perPageOptions"
                :key="size"
                @click="setPerPage(size)">
                <v-list-item-title>{{ size }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <span class="dn-range">{{ rangeStart }}–{{ rangeEnd }} of {{ formatNumber(activeCount) }}</span>
        <div class="dn-pager">
          <button
            title="Previous"
            class="dn-pager__btn"
            :disabled="page <= 1 || getting"
            @click="prevPage()">
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            title="Next"
            class="dn-pager__btn"
            :disabled="page >= totalPages || getting"
            @click="nextPage()">
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- CREATE BY PO DIALOG -->
    <v-dialog
      v-model="showPODialog"
      max-width="440px"
      content-class="dn-dialog">
      <div class="dn-modal">
        <div class="dn-modal__head">
          <div>
            <h2 class="dn-modal__title">
              Create by PO
            </h2>
            <p class="dn-modal__subtitle">
              Enter a purchase order number to start
            </p>
          </div>
          <button
            class="dn-modal__close"
            title="Close"
            @click="showPODialog = false">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round">
              <line
                x1="18"
                y1="6"
                x2="6"
                y2="18" />
              <line
                x1="6"
                y1="6"
                x2="18"
                y2="18" />
            </svg>
          </button>
        </div>

        <form
          class="dn-modal__body"
          @submit.prevent="getPurchaseOrder()">
          <label class="dn-field__label">Purchase Order No.</label>
          <div class="dn-modal__search">
            <input
              v-model="purchaseOrderId"
              class="dn-modal__input"
              placeholder="POG2024XXXXXXX"
              :disabled="loading"
              autofocus />
            <button
              type="submit"
              class="dn-modal__go"
              :disabled="loading || !purchaseOrderId"
              title="Continue">
              <v-progress-circular
                v-if="loading"
                indeterminate
                size="16"
                width="2" />
              <svg
                v-else
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <line
                  x1="5"
                  y1="12"
                  x2="19"
                  y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </form>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { getRole } from '@/assets/js/Authentication'
import InventoryProvider from '@/resources/InventoryProvider'
import AutoDeliveryNotesProvider from '@/resources/AutoDeliveryNotesProvider'
import PurchaseOrdersProvider from '@/resources/PurchaseOrdersProvider'

const InventoryService = new InventoryProvider()
const AutoDeliveryNotesService = new AutoDeliveryNotesProvider()
const PurchaseOrdersService = new PurchaseOrdersProvider()

const TYPE_COLORS = {
  ALI: { bg: '#e6eefb', fg: '#2d52b8' },
  TFI: { bg: '#fdeede', fg: '#c2701a' },
  GRI: { bg: '#e8f3ec', fg: '#1f8a50' }
}

const CHIP_COLORS = {
  origin: { bg: '#e1f0f7', fg: '#0d6a8f' },
  dest: { bg: '#f1e9f4', fg: '#824a8f' }
}

export default {
  name: 'DeliveryNoteNew',
  data () {
    return {
      tabsTitle: [
        { key: 'pending', label: 'Pending', count: 0 },
        { key: 'approved', label: 'Approved', count: 0 },
        { key: 'canceled', label: 'Cancelled', count: 0 }
      ],
      tabs: 0,
      searchInput: '',
      searchTimeout: null,
      typeFilter: 'all',
      page: 1,
      perPage: 25,
      perPageOptions: [25, 50, 100],
      totalPages: 1,
      getting: false,
      loadingCount: false,
      deliveryNotesData: [],
      selected: {},
      warehouseSelected: null,
      showPODialog: false,
      purchaseOrderId: '',
      loading: false,
      copiedId: null,
      copiedTimeout: null,
      exporting: false
    }
  },
  computed: {
    ...mapGetters({
      warehouse: 'Store/warehouse',
      mapWarehouse: 'Store/mapWarehouse'
    }),
    warehouses () {
      return this.mapWarehouse.filter((v) => v.id !== 0)
    },
    warehouseName () {
      const found = this.warehouses.find((w) => w.id === this.warehouseSelected)
      return found ? found.name : 'Select warehouse'
    },
    noteStatus () {
      return this.tabsTitle[this.tabs].key
    },
    rows () {
      if (this.typeFilter === 'all') {
        return this.deliveryNotesData
      }
      return this.deliveryNotesData.filter((item) => this.docType(item) === this.typeFilter)
    },
    typeOptions () {
      const prefixes = [...new Set(this.deliveryNotesData.map((item) => this.docType(item)))]
        .filter(Boolean)
        .sort()
      return [{ value: 'all', label: 'All' }, ...prefixes.map((p) => ({ value: p, label: p }))]
    },
    typeLabel () {
      return this.typeFilter === 'all' ? 'All' : this.typeFilter
    },
    activeCount () {
      return this.tabsTitle[this.tabs].count || 0
    },
    rangeStart () {
      return this.activeCount === 0 ? 0 : (this.page - 1) * this.perPage + 1
    },
    rangeEnd () {
      return Math.min(this.page * this.perPage, this.activeCount)
    },
    isEmpty () {
      return this.rows.length === 0
    },
    canSelect () {
      return this.noteStatus !== 'canceled'
    },
    canCancel () {
      return this.noteStatus === 'pending' && !this.isDisabledByRole
    },
    selectedItems () {
      return this.deliveryNotesData.filter((item) => this.selected[item.id])
    },
    selectedCount () {
      return this.selectedItems.length
    },
    hasSelection () {
      return this.selectedCount > 0
    },
    canPrint () {
      return this.hasSelection && this.canSelect
    },
    allSelected () {
      const visible = this.rows
      return visible.length > 0 && visible.every((item) => this.selected[item.id])
    },
    isDisabled () {
      return this.warehouseSelected === 0 || this.warehouseSelected === null
    },
    disableAllowCate () {
      const allowRole = [
        'management',
        'inventory_and_costing',
        'developer',
        'merchandising_planner',
        'merchandiser',
        'accounting_manager',
        'accounting',
        'support',
        'warehouse_manager'
      ]
      const role = getRole()
      const isAllowedRole = allowRole.some((r) => r === role)
      return !isAllowedRole || this.noteStatus !== 'approved' || this.warehouseSelected === 0 || this.warehouseSelected === null
    },
    isDisabledByRole () {
      const dnRole = [
        'management',
        'area_manager',
        'marketing',
        'developer',
        'creative',
        'vm',
        'merchandising_planner',
        'merchandiser',
        'online_admin',
        'accounting_manager',
        'warehouse_manager',
        'warehouse',
        'store_manager',
        'acting_assist_store_manager',
        'assist_store_manager',
        'sales_staff'
      ]
      const role = getRole()
      return !dnRole.some((r) => r === role)
    }
  },
  watch: {
    warehouseSelected () {
      this.resetAndFetch()
    },
    warehouses (val) {
      if (!this.warehouseSelected && val.length >= 1) {
        this.warehouseSelected = val[0].id
      }
    }
  },
  mounted () {
    if (!this.warehouseSelected && this.warehouses.length >= 1) {
      this.warehouseSelected = this.warehouses[0].id
    } else if (this.warehouseSelected !== null) {
      this.resetAndFetch()
    }
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage',
      setModal: 'Components/setModal'
    }),
    formatNumber (n) {
      return Number(n || 0).toLocaleString('en-US')
    },
    formatDate (date) {
      return this.$dayjs(date).format('DD MMM YYYY')
    },
    formatTime (date) {
      return this.$dayjs(date).format('HH:mm:ss')
    },
    dueDateClass (date) {
      const diff = this.$dayjs(date).startOf('day').diff(this.$dayjs().startOf('day'), 'day')
      if (diff > 0) {
        return 'dn-due--future'
      }
      if (diff === 0) {
        return 'dn-due--today'
      }
      return 'dn-due--past'
    },
    docType (item) {
      return (item.code || '').slice(0, 3)
    },
    docNumber (item) {
      return (item.code || '').slice(3)
    },
    typeStyle (item) {
      const tc = TYPE_COLORS[this.docType(item)] || { fg: '#52606d' }
      return { color: tc.fg }
    },
    chipStyle (role) {
      const color = CHIP_COLORS[role] || CHIP_COLORS.dest
      return { background: color.bg, color: color.fg }
    },
    currentWarehouseName (item) {
      const found = this.warehouse.find((w) => +w.id === +(item.warehouse && item.warehouse.id))
      return found ? found.name : (item.warehouse && item.warehouse.name) || ''
    },
    sourceChip (item) {
      if (item.withdrawNoteRef && item.withdrawNoteRef.warehouseId) {
        const found = this.warehouse.find((w) => +w.id === +item.withdrawNoteRef.warehouseId)
        if (found) {
          return { id: found.id, name: found.name }
        }
      }
      return null
    },
    noteCompute (item) {
      let refs = ''
      if (item.withdrawNoteRef && item.withdrawNoteRef.code) {
        refs = `Withdraw Note : ${item.withdrawNoteRef.code}`
      } else if (item.deliveryNoteRef && item.deliveryNoteRef.code) {
        refs = `Delivery Note : ${item.deliveryNoteRef.code}`
      }

      let text = ''
      if (item.note && refs) {
        text = `${item.note} | from ${refs}`
      } else if (refs) {
        text = `from ${refs}`
      } else if (item.note) {
        text = item.note
      }

      return text
    },
    isSelected (item) {
      return !!this.selected[item.id]
    },
    toggleSelect (item) {
      this.$set(this.selected, item.id, !this.selected[item.id])
    },
    toggleAll () {
      const next = { ...this.selected }
      const target = !this.allSelected
      this.rows.forEach((item) => {
        if (target) {
          next[item.id] = true
        } else {
          delete next[item.id]
        }
      })
      this.selected = next
    },
    clearSelection () {
      this.selected = {}
    },
    changeTab (index) {
      if (this.tabs === index) {
        return
      }
      this.tabs = index
      this.resetAndFetch()
    },
    setWarehouse (id) {
      if (id !== undefined && id !== null) {
        this.warehouseSelected = id
      }
    },
    setPerPage (size) {
      if (this.perPage === size) {
        return
      }
      this.perPage = size
      this.resetAndFetch(false)
    },
    onSearchInput () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.onSearch()
      }, 450)
    },
    onSearch () {
      clearTimeout(this.searchTimeout)
      this.resetAndFetch()
    },
    prevPage () {
      if (this.page > 1) {
        this.page--
        this.fetchDeliveryNote()
      }
    },
    nextPage () {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchDeliveryNote()
      }
    },
    resetAndFetch (withCount = true) {
      if (this.warehouseSelected === null) {
        return
      }
      this.page = 1
      this.totalPages = 1
      this.selected = {}
      this.deliveryNotesData = []
      if (withCount) {
        this.fetchCount()
      }
      this.fetchDeliveryNote()
    },
    async fetchCount () {
      try {
        this.loadingCount = true
        const { data } = await InventoryService.getDeliveryNotesCount({
          search: this.searchInput,
          warehouse: this.warehouseSelected
        })
        for (const element of this.tabsTitle) {
          element.count = data[element.key]
        }
      } catch (error) {
        console.error('fetchCount', error)
      } finally {
        this.loadingCount = false
      }
    },
    async fetchDeliveryNote () {
      try {
        this.getting = true
        const { data } = await InventoryService.getDeliveryNotes({
          page: this.page,
          limit: this.perPage,
          search: this.searchInput,
          status: this.noteStatus,
          warehouse: this.warehouseSelected,
          sortBy: 'createdAt',
          sortOrder: this.noteStatus === 'pending' ? 'asc' : 'desc'
        })
        this.totalPages = data.pages || 1
        this.deliveryNotesData = await this.mapAutoDeliveryNote(data.results)
      } catch (error) {
        console.error('fetchDeliveryNote', error)
        this.setErrorPage(error.code)
      } finally {
        this.getting = false
      }
    },
    async mapAutoDeliveryNote (deliveryNotes) {
      if (!deliveryNotes || deliveryNotes.length === 0) {
        return []
      }
      try {
        const dnIds = deliveryNotes.map((note) => note.id)
        const { data: response } = await AutoDeliveryNotesService.getAutoDeliveryNotes(dnIds)
        return deliveryNotes.map((note) => ({
          ...note,
          autoDeliveryNoteId: response.find((n) => n.deliveryNoteId === note.id)?.deliveryNoteId
        }))
      } catch (error) {
        console.error('mapAutoDeliveryNote', error)
        return deliveryNotes
      }
    },
    async copyCode (item) {
      try {
        await navigator.clipboard.writeText(item.code || '')
        clearTimeout(this.copiedTimeout)
        this.copiedId = item.id
        this.copiedTimeout = setTimeout(() => {
          this.copiedId = null
        }, 1500)
      } catch (error) {
        console.error('copyCode', error)
        this.setSnackbar({
          value: true,
          message: 'Copy failed',
          type: 'error'
        })
      }
    },
    openDetail (item) {
      const routeData = this.$router.resolve({
        name: 'DeliveryNote',
        query: {
          id: item.id,
          warehouse: this.warehouseSelected
        }
      })
      window.open(routeData.href, '_blank')
    },
    openAllocation (item) {
      if (this.disableAllowCate) {
        return
      }
      const query = item.autoDeliveryNoteId
        ? { noteCode: item.code, autoDeliveryNoteId: item.autoDeliveryNoteId }
        : { noteId: item.id, warehouseId: this.warehouseSelected }
      const routeData = this.$router.resolve({
        name: 'MoveProducts',
        query
      })
      window.open(routeData.href, '_blank')
    },
    printOne (item) {
      window.open(
        this.$router.resolve({
          name: 'MultiDeliveryNote',
          query: {
            id: [item.id],
            warehouse: [item.warehouse.id]
          }
        }).href,
        '_blank'
      )
    },
    printSelected () {
      if (!this.canPrint) {
        return
      }
      const id = []
      const warehouse = []
      this.selectedItems.forEach((item) => {
        id.push(item.id)
        warehouse.push(item.warehouse.id)
      })
      window.open(
        this.$router.resolve({
          name: 'MultiDeliveryNote',
          query: { id, warehouse }
        }).href,
        '_blank'
      )
    },
    async exportItems () {
      if (!this.hasSelection || this.exporting) {
        return
      }
      try {
        this.exporting = true
        const items = [...this.selectedItems]
        const details = await Promise.all(items.map((item) => InventoryService.getDeliveryNoteById(item.id)))

        const nameOf = (v) => (v && typeof v === 'object' ? (v.name || '') : (v || ''))

        const skuMap = {}
        details.forEach(({ data }, index) => {
          const docCode = (data && data.code) || items[index].code
          const products = (data && data.products) || []
          products.forEach((product) => {
            (product.skus || []).forEach((sku) => {
              const code = sku.code || ''
              if (!code) {
                return
              }
              if (!skuMap[code]) {
                skuMap[code] = {
                  code,
                  name: product.name || '',
                  color: nameOf(sku.color),
                  size: nameOf(sku.size),
                  count: 0,
                  amount: 0,
                  docs: new Set()
                }
              }
              skuMap[code].count += Number(sku.count || 0)
              skuMap[code].amount += Number(sku.amount || 0)
              skuMap[code].docs.add(docCode)
            })
          })
        })

        const rows = Object.values(skuMap)
        if (!rows.length) {
          this.setSnackbar({
            value: true,
            message: '\u0e44\u0e21\u0e48\u0e1e\u0e1a SKU \u0e43\u0e19\u0e43\u0e1a\u0e17\u0e35\u0e48\u0e40\u0e25\u0e37\u0e2d\u0e01',
            type: 'error'
          })
          return
        }

        const esc = (v) => String(v || '').replace(/"/g, '""')
        const header = ['Product Name', 'SKU Code', 'Color', 'Size', 'Amount', 'Count', 'Document No.']
        const lines = rows.map((row) => {
          const docs = [...row.docs].join(',')
          return [
            `"${esc(row.name)}"`,
            `"${esc(row.code)}"`,
            `"${esc(row.color)}"`,
            `"${esc(row.size)}"`,
            row.amount,
            row.count,
            `"${esc(docs)}"`
          ].join(',')
        })
        const csv = [header.join(','), ...lines].join('\n')
        const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `delivery-note-items-${this.$dayjs().format('YYYYMMDD-HHmmss')}.csv`
        link.click()
        URL.revokeObjectURL(url)
      } catch (error) {
        console.error('exportItems', error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.exporting = false
      }
    },
    createNote () {
      const routeData = this.$router.resolve({
        name: 'DeliveryNote',
        query: { warehouse: this.warehouseSelected }
      })
      window.open(routeData.href, '_blank')
    },
    goToOldVersion () {
      this.$router.push({ name: 'DeliveryNoteList' })
    },
    togglePODialog () {
      this.showPODialog = !this.showPODialog
    },
    cancelSelected () {
      this.setModal({
        value: true,
        title: `ยืนยันการยกเลิก ${this.selectedCount} รายการ`,
        message: 'การยกเลิกใบนำสินค้าเข้า(DN) จะไปยกเลิกใบนำสินค้าออก(WN)ด้วย โปรดตรวจสอบให้ถูกต้องก่อนการกดยกเลิก',
        confirmText: 'Cancel',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Back',
        onConfirm: () => this.doCancelSelected()
      })
    },
    async doCancelSelected () {
      const items = [...this.selectedItems]
      try {
        await Promise.all(items.map(async (item) => {
          const { data } = await InventoryService.updateStatusDeliveryNotes({
            id: item.id,
            status: 'cancel'
          })
          if (data.status === 'canceled' && item.withdrawNoteRef && item.withdrawNoteRef.id) {
            await InventoryService.updateStatusWithdrawNotes({
              id: item.withdrawNoteRef.id,
              status: 'cancel'
            })
          }
        }))
        this.setSnackbar({
          value: true,
          message: 'Cancelled successfully',
          type: 'success'
        })
      } catch (error) {
        console.error('doCancelSelected', error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.resetAndFetch()
      }
    },
    async getPurchaseOrder () {
      if (!this.purchaseOrderId) {
        return
      }
      try {
        this.loading = true
        const { data } = await PurchaseOrdersService.getItemByOrderId(this.purchaseOrderId)

        if (data && ['approved', 'received'].includes(data?.state)) {
          const foundWarehouse = this.warehouse.find((w) => w.code === data.locationCode)

          if (!foundWarehouse) {
            throw Error('Warehouse not found.')
          }

          const warehouse = data.type === 'RM' ? this.warehouseSelected : foundWarehouse.id
          const routeData = this.$router.resolve({ name: 'DeliveryNote', query: { poNo: data.orderNo, warehouse } })
          window.open(routeData.href, '_blank')
        } else if (data && data?.state === 'completed') {
          this.setSnackbar({
            value: true,
            message: 'PO ใบนี้รับสำเร็จแล้ว',
            type: 'success'
          })
        } else if (data && !(['approved', 'received'].includes(data?.state))) {
          this.setSnackbar({
            value: true,
            message: 'PO ใบนี้ยังไม่ได้รับการอนุมัติ กรุณาติดต่อผู้ที่เกี่ยวข้อง',
            type: 'error'
          })
        } else {
          this.setSnackbar({
            value: true,
            message: 'Purchase order not found.',
            type: 'error'
          })
        }
      } catch (error) {
        console.error('getPurchaseOrder', error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

$primary: #0f5d7c;
$ink: #1f2933;
$muted: #7b8794;
$faint: #9aa5b1;

.dn-new {
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  color: $ink;
  -webkit-font-smoothing: antialiased;
  width: 100%;
  padding: 6px 4px 40px;

  * {
    box-sizing: border-box;
  }
}

.dn-switch-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding: 10px 16px;
  border: 1px solid #f3a9d4;
  background: #faf0f6;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f7e6f1;
  }

  &__text {
    font-size: 14px;
    font-weight: 600;
    color: #16160f;
  }

  &__cta {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #16160f;
  }
}

.dn-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}

.dn-title {
  margin: 0;
  font-size: 27px;
  font-weight: 700;
  letter-spacing: -0.4px;
  color: #1a2b34;
}

.dn-subtitle {
  margin: 7px 0 0;
  font-size: 14px;
  color: $muted;
}

.dn-header-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

.dn-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &--outline {
    border: 1.5px solid $primary;
    background: #fff;
    color: $primary;

    &:hover:not(:disabled) {
      background: #f0f7fa;
    }
  }

  &--primary {
    border: 1.5px solid $primary;
    background: $primary;
    color: #fff;
    padding: 0 22px;
    box-shadow: 0 2px 6px rgba(15, 93, 124, 0.25);

    &:hover:not(:disabled) {
      background: #0c4d68;
    }
  }
}

.dn-card {
  background: #fff;
  border: 1px solid #e4e8ec;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(16, 42, 67, 0.04), 0 8px 24px rgba(16, 42, 67, 0.04);
  overflow: hidden;
}

.dn-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  padding: 20px 24px;
  border-bottom: 1px solid #eef1f4;
}

.dn-field {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &__label {
    font-size: 11.5px;
    font-weight: 600;
    color: $faint;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.dn-autocomplete {
  min-width: 230px;
  font-family: inherit;

  ::v-deep .v-input__slot {
    min-height: 42px !important;
    padding: 0 14px !important;
    border: 1.5px solid #dce1e6 !important;
    background: #fff !important;
    border-radius: 10px !important;
    box-shadow: none !important;
    transition: border-color 0.15s;
  }

  ::v-deep .v-input__slot:hover {
    border-color: #b6c0c9 !important;
  }

  &.v-input--is-focused ::v-deep .v-input__slot {
    border-color: $primary !important;
    box-shadow: 0 0 0 3px rgba(15, 93, 124, 0.12) !important;
  }

  ::v-deep input {
    font-size: 14px;
    font-weight: 600;
    color: $ink;

    &::placeholder {
      font-weight: 500;
      color: $faint;
    }
  }

  ::v-deep .v-select__selection {
    font-size: 14px;
    font-weight: 600;
    color: $ink;
  }

  ::v-deep .v-input__append-inner {
    margin-top: 0 !important;
    align-self: center;

    .v-icon {
      font-size: 20px;
      color: $muted;
    }
  }
}

.dn-spacer {
  flex: 1;
}

.dn-search {
  position: relative;
  display: flex;
  align-items: center;

  &__icon {
    position: absolute;
    left: 14px;
    pointer-events: none;
  }

  &__input {
    width: 320px;
    max-width: 100%;
    height: 42px;
    padding: 0 14px 0 40px;
    border: 1.5px solid #dce1e6;
    background: #fff;
    border-radius: 10px;
    font-size: 14px;
    font-family: inherit;
    color: $ink;
    outline: none;

    &:focus {
      border-color: $primary;
      box-shadow: 0 0 0 3px rgba(15, 93, 124, 0.12);
    }
  }
}

.dn-tabs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  border-bottom: 1px solid #eef1f4;
  flex-wrap: wrap;
  gap: 8px;
}

.dn-tabs {
  display: flex;
  gap: 4px;
}

.dn-tab {
  display: flex;
  align-items: center;
  gap: 9px;
  height: 52px;
  padding: 0 8px;
  margin: 0 8px;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
  font-weight: 600;
  color: $muted;
  border-bottom: 2.5px solid transparent;

  &--active {
    font-weight: 700;
    color: $primary;
    border-bottom-color: $primary;

    .dn-tab__badge {
      background: #e1f0f7;
      color: #0d6a8f;
    }
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    padding: 1px 9px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    background: #f0f3f6;
    color: $faint;
  }
}

.dn-tabs-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dn-action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 16px;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  border: 1px solid #cfe0e8;
  background: #fff;
  color: $primary;
  cursor: pointer;

  &:disabled {
    border-color: #e4e8ec;
    background: #f4f6f8;
    color: #b6c0c9;
    cursor: not-allowed;
  }

  &--primary {
    border-color: $primary;
    background: $primary;
    color: #fff;

    &:disabled {
      border-color: #e4e8ec;
      background: #f4f6f8;
      color: #b6c0c9;
    }
  }
}

.dn-sel-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 24px;
  background: #eef7fb;
  border-bottom: 1px solid #d6e9f2;

  &__text {
    font-size: 13.5px;
    font-weight: 600;
    color: $primary;
  }
}

.dn-sel-btn {
  height: 34px;
  padding: 0 14px;
  border: 1px solid #cdd9e0;
  background: #fff;
  color: #52606d;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;

  &--danger {
    border-color: #e7b9b9;
    color: #d23b3b;

    &:hover {
      background: #fdf2f2;
    }
  }

  &--solid {
    border: none;
    background: $primary;
    color: #fff;
    padding: 0 16px;
  }
}

.dn-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 34px;
  padding: 12px 28px;
  background: #fafbfc;
  flex-wrap: wrap;

  &--top {
    border-bottom: 1px solid #eef1f4;
  }

  &--footer {
    border-top: 1px solid #eef1f4;
    padding: 14px 28px;
  }
}

.dn-rows-per-page {
  display: flex;
  align-items: center;
  gap: 12px;

  &__label {
    font-size: 13px;
    color: $muted;
  }

  &__btn {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    padding: 0 6px 0 8px;
    border: none;
    border-bottom: 1px solid #cbd2d9;
    background: transparent;
    font-size: 13.5px;
    font-weight: 600;
    color: #3e4c59;
    font-family: inherit;
    cursor: pointer;

    &:hover {
      border-bottom-color: $primary;
    }
  }
}

.dn-range {
  font-size: 13.5px;
  color: #52606d;
  font-variant-numeric: tabular-nums;
}

.dn-pager {
  display: flex;
  align-items: center;
  gap: 4px;

  &__btn {
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: #52606d;
    border-radius: 8px;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #eef1f4;
    }

    &:disabled {
      color: #b6c0c9;
      cursor: not-allowed;
    }
  }
}

.dn-table-scroll {
  overflow-x: auto;
}

.dn-table {
  min-width: 1120px;
}

.dn-row {
  display: grid;
  grid-template-columns: 46px 300px 148px 148px minmax(260px, 1fr) 116px;
  align-items: center;
  column-gap: 16px;
  padding: 15px 24px;
  border-bottom: 1px solid #f0f3f5;
  background: #fff;
  transition: background 0.12s;

  &:hover:not(&--head) {
    background: #f7fafb;
  }

  &--head {
    padding: 13px 24px;
    background: #fafbfc;
    border-bottom: 1px solid #eef1f4;
  }

  &--selected {
    background: #eef7fb;

    &:hover {
      background: #eef7fb;
    }
  }
}

.dn-th {
  font-size: 11.5px;
  font-weight: 700;
  color: $muted;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &--right {
    text-align: right;
  }

  &--center {
    text-align: center;
  }
}

.dn-cell--center {
  text-align: center;
}

.dn-cell-check {
  display: flex;
  align-items: center;
}

.dn-check {
  width: 19px;
  height: 19px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1.5px solid #cbd2d9;
  background: #fff;
  transition: all 0.12s;

  &--on {
    border-color: $primary;
    background: $primary;
  }
}

.dn-doc {
  display: flex;
  align-items: center;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: -0.2px;

  &__type {
    display: inline-flex;
    align-items: center;
    margin-right: 7px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.4px;
  }

  &__code {
    color: $primary;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__copy {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-left: 6px;
    padding: 0;
    border: none;
    background: transparent;
    color: $faint;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.12s, color 0.12s;

    &:hover {
      background: #eef1f4;
      color: $primary;
    }
  }
}

.dn-loc {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin-top: 9px;

  &__chip {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    white-space: nowrap;
    height: 21px;
    padding: 0 8px;
    border-radius: 6px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }

  &__arrow {
    flex-shrink: 0;
  }
}

.dn-date {
  font-size: 13px;
  font-weight: 500;
  color: #3e4c59;

  &--empty {
    color: $faint;
  }
}

.dn-due {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 7px;
  font-weight: 600;

  &--future {
    background: #e4f6ea;
    color: #1f8a50;
  }

  &--today {
    background: #fdf3d7;
    color: #b7791f;
  }

  &--past {
    background: #fdecec;
    color: #d23b3b;
  }
}

.dn-time {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  color: $faint;
  margin-top: 2px;
}

.dn-note {
  font-size: 13px;
  color: #52606d;
  line-height: 1.45;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.dn-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.dn-icon-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dce1e6;
  background: #fff;
  color: $muted;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #f7f9fa;
    color: #52606d;
  }

  &--accent {
    border-color: #cfe0e8;
    color: $primary;

    &:hover {
      background: #f0f7fa;
      border-color: #9fc4d4;
      color: $primary;
    }
  }

  &--allocated {
    border-color: #b6e0c6;
    background: #e8f3ec;
    color: #1f8a50;

    &:hover {
      background: #daeee1;
      border-color: #8fd0a8;
      color: #1f8a50;
    }
  }

  &:disabled {
    border-color: #e4e8ec;
    background: #f4f6f8;
    color: #cbd2d9;
    cursor: not-allowed;

    &:hover {
      background: #f4f6f8;
      border-color: #e4e8ec;
      color: #cbd2d9;
    }
  }
}

.dn-empty {
  padding: 64px 24px;
  text-align: center;
  color: $faint;

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $muted;
  }

  &__sub {
    font-size: 13px;
    margin-top: 6px;
  }
}

::v-deep .dn-dialog {
  border-radius: 16px;
  box-shadow: 0 12px 48px rgba(16, 42, 67, 0.22);
}

.dn-modal {
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 22px 24px 18px;
    border-bottom: 1px solid #eef1f4;
  }

  &__title {
    margin: 0;
    font-size: 19px;
    font-weight: 700;
    letter-spacing: -0.3px;
    color: #1a2b34;
  }

  &__subtitle {
    margin: 5px 0 0;
    font-size: 13px;
    color: $muted;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: $muted;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background: #eef1f4;
      color: #52606d;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 22px 24px;
  }

  &__search {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__go {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border: 1.5px solid $primary;
    background: $primary;
    color: #fff;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s, opacity 0.15s;

    &:hover:not(:disabled) {
      background: #0c4d68;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__input {
    flex: 1;
    height: 44px;
    padding: 0 14px;
    border: 1.5px solid #dce1e6;
    background: #fff;
    border-radius: 10px;
    font-size: 14px;
    font-family: 'IBM Plex Mono', monospace;
    color: $ink;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;

    &:focus {
      border-color: $primary;
      box-shadow: 0 0 0 3px rgba(15, 93, 124, 0.12);
    }

    &:disabled {
      background: #f4f6f8;
    }
  }
}

.dn-icon-btn--printed {
  color: #2e7d32;
}
</style>
